import styled from "styled-components"

export const Container = styled.div`
  margin-top: 3rem;
  border-bottom: 0.1rem solid ${p => p.theme.colors.borderLight};
`

export const TableHeader = styled.div`
  margin-top: 1rem;
  display: grid;
  background-color: ${({ theme }) => theme.colors.white};
  border: 0.1rem solid ${({ theme }) => theme.colors.borderLight};
  border-left: none;
  grid-template-columns: 5fr 1fr 1fr 1fr;

  ${(p) => p.theme.mQ.MEDIA_MIN_LARGE} {
    grid-template-columns: 3.5fr 1.5fr 1.5fr 1fr;
  }

  ${(p) => p.theme.mQ.MEDIA_MIN_X_LARGE} {
    grid-template-columns: 3fr 3fr 1fr 1.5fr 1fr 1fr 1fr 0.8fr;
  }
`

export const Section = styled.div`
  font-weight: 700;
  padding: 1rem;
  border-left: 0.1rem solid ${({ theme }) => theme.colors.borderLight};
  display: flex;
  align-items: center;
  justify-content: center;
  height: 5rem;
`

export const CheckoutHeader = styled(Section)`
  justify-content: flex-start;
`

export const TableRow = styled(TableHeader)`
  margin-top: 0;
  transition: background-color 0.2s ease;

  div {
    font-weight: 400;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.greyLightest};
  }

  &:hover .expand-button {
    background: ${({ theme }) => theme.colors.turquoiseLighter};
    border-color: ${({ theme }) => theme.colors.turquoise};
  }
`

export const CheckoutCell = styled(Section)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding-left: 2rem;
  min-width: 0; /* Allow flex item to shrink below content size */
  overflow: hidden; /* Prevent overflow */
  word-wrap: break-word; /* Break long words */
  word-break: break-word; /* Break long words */
`

export const CheckoutId = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 0.3rem;
  color: ${({ theme }) => theme.colors.turquoiseDark};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  word-break: break-all;
  overflow-wrap: break-word;
`

export const CustomerEmail = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  word-break: break-all;
  overflow-wrap: break-word;
  line-height: 1.3;
`

export const DateTime = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`

export const TotalAmount = styled.div`
  font-weight: 600;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.black};
`

export const ItemCount = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
  margin-top: 0.2rem;
  white-space: nowrap; /* Keep item count on one line */
`

export const OrderReference = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 0.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  word-break: break-all;
  overflow-wrap: break-word;
  line-height: 1.3;
`

export const PaymentStatusBadge = styled.div<{ $status: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  cursor: help;
  transition: all 0.2s ease;
  position: relative;

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }

  &:hover {
    transform: scale(1.1);
  }

  ${props => {
    switch (props.$status) {
      case 'FAILED':
      case 'REJECTED':
      case 'DECLINED':
        return `
          background: #fbe7e6;
          border: 0.1rem solid #d53538;

          svg {
            fill: #d53538;
          }
        `;
      case 'CANCELLED':
        return `
          background: ${props.theme.colors.greyLighter};
          border: 0.1rem solid ${props.theme.colors.greyDark};

          svg {
            fill: ${props.theme.colors.greyDark};
          }
        `;
      case 'PENDING':
      case 'ON_HOLD':
        return `
          background: #fff6e5;
          border: 0.1rem solid #ffb732;

          svg {
            fill: #ffb732;
          }
        `;
      case 'STARTED':
        return `
          background: ${props.theme.colors.blueLight};
          border: 0.1rem solid ${props.theme.colors.blue};

          svg {
            fill: ${props.theme.colors.blue};
            padding-left: 0.2rem;
          }
        `;
      case 'COMPLETED':
        return `
          background: #e0f3e5;
          border: 0.1rem solid #7fcd6a;

          svg {
            fill: #7fcd6a;
          }
        `;
      default:
        return `
          background: ${props.theme.colors.greyLighter};
          border: 0.1rem solid ${props.theme.colors.greyDark};

          svg {
            fill: ${props.theme.colors.greyDark};
          }
        `;
    }
  }}
`

export const ExpandButton = styled.button<{ $expanded: boolean }>`
  background: ${({ theme }) => theme.colors.greyLightest};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 50%;
  cursor: pointer;
  padding: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.turquoiseLighter};
    border-color: ${({ theme }) => theme.colors.turquoise};
    transform: scale(1.05);
  }

  svg {
    width: 1.4rem;
    height: 1.4rem;
    transition: transform 0.3s ease;
    transform: ${props => props.$expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
    color: ${({ theme }) => theme.colors.greyDark};
  }

  &:hover svg {
    color: ${({ theme }) => theme.colors.turquoiseDark};
  }
`

export const ExpandableContent = styled.div`
  background-color: ${({ theme }) => theme.colors.greyLightest};
  border-left: 4px solid ${({ theme }) => theme.colors.turquoiseDark};
  margin-top: 0;
`

export const OrderLinesHeader = styled.div`
  padding: 1rem 2rem;
  font-weight: 600;
  font-size: 1.1rem;
  border-bottom: 2px solid ${({ theme }) => theme.colors.borderLight};
  margin-left: 3rem;
  color: ${({ theme }) => theme.colors.black};
`

export const OrderLinesTableHeader = styled.div`
  display: grid;
  grid-template-columns: 20rem 6rem 8rem 12rem 1fr;
  align-items: center;
  padding: 0.6rem 0;
  margin-left: 4rem;
  background-color: ${({ theme }) => theme.colors.greyLighter};
  font-weight: 600;
  font-size: 1.0rem;
  color: ${({ theme }) => theme.colors.greyDarker};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`

export const CheckoutDetailsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  min-width: 0; /* Allow flex item to shrink */
  height: 100%;

  > div {
    flex: 1;
    min-width: 0; /* Allow text container to shrink */
    overflow: hidden;
  }
`

export const CopyButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.2rem;
  display: flex;
  align-items: center;
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`
